import { useState, useEffect } from "react";
import { useGraphQLAuth } from "hooks/useGraphQLAuth";
import { useToast } from "hooks/useToast";

interface TrackFormData {
    name: string;
    description: string;
    color: string;
    colorText: string;
}

interface TrackFormErrors {
    name?: string;
    description?: string;
    color?: string;
    colorText?: string;
}

interface TrackFormTouched {
    name: boolean;
    description: boolean;
    color: boolean;
    colorText: boolean;
}

export const useTrackForm = (
    onSuccess: () => void,
    editingTrack: any,
    eventId?: string
) => {
    const { connectGraphQLApi } = useGraphQLAuth();
    const { setToast } = useToast();

    const [inputsValues, setInputsValues] = useState<TrackFormData>({
        name: "",
        description: "",
        color: "#3A3956",
        colorText: "#FFFFFF",
    });

    const [touched, setTouched] = useState<TrackFormTouched>({
        name: false,
        description: false,
        color: false,
        colorText: false,
    });

    const [errors, setErrors] = useState<TrackFormErrors>({});
    const [isLoading, setIsLoading] = useState(false);
    const [isEditing, setIsEditing] = useState(false);

    // Load editing track data
    useEffect(() => {
        if (editingTrack) {
            setIsEditing(true);
            setInputsValues({
                name: editingTrack.name || "",
                description: editingTrack.description || "",
                color: editingTrack.color || "#3A3956",
                colorText: editingTrack.colorText || "#FFFFFF",
            });
        }
    }, [editingTrack]);

    const validateForm = (): TrackFormErrors => {
        const newErrors: TrackFormErrors = {};

        if (!inputsValues.name.trim()) {
            newErrors.name = "Nome é obrigatório";
        }

        if (!inputsValues.color) {
            newErrors.color = "Cor é obrigatória";
        }

        if (!inputsValues.colorText) {
            newErrors.colorText = "Cor do texto é obrigatória";
        }

        return newErrors;
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        
        setInputsValues(prev => ({
            ...prev,
            [name]: value,
        }));

        // Mark field as touched
        setTouched(prev => ({
            ...prev,
            [name]: true,
        }));

        // Clear error for this field
        if (errors[name as keyof TrackFormErrors]) {
            setErrors(prev => ({
                ...prev,
                [name]: undefined,
            }));
        }
    };

    const handleSubmit = async () => {
        const validationErrors = validateForm();
        
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            setToast({
                type: "error",
                message: "Por favor, corrija os erros no formulário.",
                isActive: true,
                timeToRemoveToast: 5000,
            });
            return;
        }

        setIsLoading(true);

        try {
            const mutation = isEditing
                ? `
                    mutation BackofficeUpdateTrack($trackId: ID!, $input: TrackUpdateInput!) {
                        backofficeUpdateTrack(trackId: $trackId, input: $input) {
                            trackId
                        }
                    }
                `
                : `
                    mutation BackofficeCreateTrack($input: TrackInput!) {
                        backofficeCreateTrack(input: $input) {
                            trackId
                        }
                    }
                `;

            const variables = isEditing
                ? { 
                    trackId: editingTrack.trackId, 
                    input: {
                        name: inputsValues.name,
                        description: inputsValues.description,
                        color: inputsValues.color,
                        colorText: inputsValues.colorText,
                    }
                }
                : { 
                    input: {
                        eventId,
                        name: inputsValues.name,
                        description: inputsValues.description,
                        color: inputsValues.color,
                        colorText: inputsValues.colorText,
                    }
                };

            await connectGraphQLApi({
                query: mutation,
                variables,
                errorMessage: `Erro ao ${isEditing ? 'atualizar' : 'criar'} trilha`,
                useNewApi: true,
            });

            setToast({
                type: "success",
                message: `Trilha ${isEditing ? 'atualizada' : 'criada'} com sucesso!`,
                isActive: true,
                timeToRemoveToast: 3000,
            });

            onSuccess();
        } catch (error) {
            setToast({
                type: "error",
                message: `Erro ao ${isEditing ? 'atualizar' : 'criar'} trilha.`,
                isActive: true,
                timeToRemoveToast: 5000,
            });
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    };

    return {
        handleInputChange,
        inputsValues,
        touched,
        errors,
        handleSubmit,
        isLoading,
        isEditing,
    };
}; 